import React, { useEffect, useRef, useCallback } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
}

export function StarryBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const animationRef = useRef<number>();
  const lastFrameTimeRef = useRef<number>(0);
  const FPS = 30; // Limit FPS to 30 for better performance
  const frameInterval = 1000 / FPS;

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Set display size
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';

    // Set actual size scaled by pixel ratio
    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;

    // Scale context to match pixel ratio
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }
  }, []);

  const createStars = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Reduce star count on mobile devices
    const isMobile = window.innerWidth <= 768;
    const baseStarCount = Math.floor((canvas.width * canvas.height) / (isMobile ? 30000 : 15000));
    const starCount = Math.min(baseStarCount, isMobile ? 100 : 200); // Cap maximum stars

    starsRef.current = [];

    for (let i = 0; i < starCount; i++) {
      starsRef.current.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 2 + 0.5,
        speed: Math.random() * 0.5 + 0.1,
        opacity: Math.random() * 0.8 + 0.2,
      });
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = (timestamp: number) => {
      // Check if enough time has passed since last frame
      const elapsed = timestamp - lastFrameTimeRef.current;

      if (elapsed < frameInterval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      lastFrameTimeRef.current = timestamp - (elapsed % frameInterval);
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      starsRef.current.forEach(star => {
        star.y += star.speed;

        if (star.y > window.innerHeight) {
          star.y = -5;
          star.x = Math.random() * window.innerWidth;
        }

        ctx.save();
        ctx.globalAlpha = star.opacity;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    // Throttled resize handler
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCanvas();
        createStars();
      }, 250); // Throttle to 250ms
    };

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      resizeCanvas();
      createStars();
      animate(0);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [resizeCanvas, createStars]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-10 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"
      style={{ pointerEvents: 'none' }}
    />
  );
}
